import React, { useEffect, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import Animated, {
  cancelAnimation,
  Easing,
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withTiming,
  type SharedValue,
} from 'react-native-reanimated';

import { hexToHsl, hslToHex } from '../../../shared/theme/colorUtils';
import type { LoadMainPalette, LoadMainVariantProps } from '../types';

const CIRCLE_COUNT = 100;
const TURNS = 6;

function resolveCircleColor(index: number, colors: LoadMainPalette): string {
  const { h: base_hue, s: base_sat } = hexToHsl(colors.primary);
  const hue_step = 360 / (CIRCLE_COUNT / TURNS);
  const hue = (base_hue + hue_step * index) % 360;
  const saturation = Math.min(Math.max(base_sat + 18, 55), 100);
  const lightness = 68;

  return hslToHex(hue, saturation, lightness);
}

function SphereCircle({
  index,
  size,
  color,
  progress,
  from_radius,
  to_radius,
  dot_size,
}: {
  index: number;
  size: number;
  color: string;
  progress: SharedValue<number>;
  from_radius: number;
  to_radius: number;
  dot_size: number;
}) {
  const rotation_deg = (TURNS * 360 / CIRCLE_COUNT) * index;

  const circle_style = useAnimatedStyle(() => {
    const staggered = (progress.value + index / CIRCLE_COUNT) % 1;
    const radius = interpolate(staggered, [0, 1], [from_radius, to_radius]);
    const scale =
      staggered < 0.25
        ? interpolate(staggered, [0, 0.25], [0, 1])
        : staggered < 0.5
          ? 1
          : interpolate(staggered, [0.5, 1], [1, 0]);

    return {
      transform: [{ rotate: `${rotation_deg}deg` }, { translateY: radius }, { scale }],
    };
  });

  return (
    <Animated.View
      style={[
        styles.circle_host,
        {
          width: size,
          height: size,
        },
        circle_style,
      ]}>
      <View
        style={{
          width: dot_size,
          height: dot_size,
          borderRadius: dot_size / 2,
          backgroundColor: color,
        }}
      />
    </Animated.View>
  );
}

export function SphereLoader({ size, colors, duration_ms }: LoadMainVariantProps) {
  const progress = useSharedValue(0);

  const from_radius = size * 0.5;
  const to_radius = size * 0.1;
  const dot_size = Math.max(2, size * 0.08);

  const circle_colors = useMemo(
    () => Array.from({ length: CIRCLE_COUNT }, (_, index) => resolveCircleColor(index, colors)),
    [colors],
  );

  useEffect(() => {
    progress.value = 0;
    progress.value = withRepeat(
      withTiming(1, { duration: duration_ms, easing: Easing.inOut(Easing.ease) }),
      -1,
      false,
    );
    return () => cancelAnimation(progress);
  }, [duration_ms, progress]);

  return (
    <View style={[styles.container, { width: size, height: size }]}>
      {circle_colors.map((color, index) => (
        <SphereCircle
          key={index}
          index={index}
          size={size}
          color={color}
          progress={progress}
          from_radius={from_radius}
          to_radius={to_radius}
          dot_size={dot_size}
        />
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  circle_host: {
    position: 'absolute',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
});
